/*
  ORTTU collection state + back-button restoration.

  Progressive enhancement for AJAX Load More / Load Previous collection pages:
  - records the clicked product handle + product page from product cards;
  - on collection return, if the product is not in the DOM yet, fetches the
    missing collection pages first;
  - scrolls to the product after DOM reconstruction, with scrollY fallback.
*/
(function () {
  if (!/\/collections\//.test(window.location.pathname)) return;
  if (window.__orttuCollectionState) return;
  window.__orttuCollectionState = true;

  var STORAGE_KEY = 'collectionScrollData';
  var MAX_AGE_MS = 10 * 60 * 1000;
  var MAX_RESTORE_PAGES = 8;
  var restoring = false;

  document.documentElement.classList.add('orttu-collection-enhanced');

  if ('scrollRestoration' in window.history) {
    try { window.history.scrollRestoration = 'manual'; } catch (e) {}
  }

  function parsePage(urlLike) {
    if (typeof urlLike === 'number') {
      return Number.isFinite(urlLike) && urlLike > 0 ? Math.floor(urlLike) : 1;
    }

    if (typeof urlLike === 'string' && /^\d+$/.test(urlLike)) {
      var directPage = parseInt(urlLike, 10);
      return directPage > 0 ? directPage : 1;
    }

    try {
      var url = new URL(urlLike || window.location.href, window.location.origin);
      var page = parseInt(url.searchParams.get('page') || '1', 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    } catch (e) {
      return 1;
    }
  }

  function sameCollection(savedUrl) {
    try {
      return new URL(savedUrl, window.location.origin).pathname === window.location.pathname;
    } catch (e) {
      return false;
    }
  }

  function getStoredData(sourceData) {
    var data = sourceData && sourceData.scrollToProduct ? sourceData : null;

    try {
      var stored = window.localStorage && window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        var parsed = JSON.parse(stored);
        if (parsed && parsed.scrollToProduct && (!data || (parsed.timestamp || 0) >= (data.timestamp || 0))) {
          data = parsed;
        }
      }
    } catch (e) {}

    if (!data || !data.scrollToProduct || !data.collectionUrl) return null;
    if (data.timestamp && Date.now() - data.timestamp > MAX_AGE_MS) return null;
    if (!sameCollection(data.collectionUrl)) return null;

    data.productPage = parsePage(data.productPage || data.collectionUrl);
    data.loadedPage = parsePage(data.loadedPage || data.collectionUrl);
    return data;
  }

  function getGrid() {
    return document.getElementById('product-grid');
  }

  function getTarget(handle) {
    if (!handle) return null;
    try {
      return document.getElementById('product-' + CSS.escape(handle));
    } catch (e) {
      return document.getElementById('product-' + handle);
    }
  }

  function getCurrentPage() {
    return parsePage(window.location.href);
  }

  function pageUrl(page) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', page);
    return url.href;
  }

  function markPageItems(fragment, page) {
    fragment.querySelectorAll('[data-product-handle]').forEach(function (el) {
      if (!el.getAttribute('data-product-page')) {
        el.setAttribute('data-product-page', String(page));
      }
    });
  }

  function loadedPages() {
    var pages = {};
    var grid = getGrid();
    if (!grid) return [];

    grid.querySelectorAll('[data-product-page]').forEach(function (el) {
      var page = parsePage(el.getAttribute('data-product-page'));
      pages[page] = true;
    });

    return Object.keys(pages).map(function (page) { return parseInt(page, 10); }).sort(function (a, b) { return a - b; });
  }

  function hasLoadedPage(page) {
    return loadedPages().indexOf(page) !== -1;
  }

  function restoreCachedPreviousPages(data) {
    var grid = getGrid();
    if (!grid || !data || !data.gridCacheKey) return false;

    try {
      var raw = window.sessionStorage && window.sessionStorage.getItem(data.gridCacheKey);
      if (!raw) return false;

      var cache = JSON.parse(raw);
      if (!cache || !cache.pages || !cache.pages.length) return false;
      if (cache.collectionPath && cache.collectionPath !== window.location.pathname) return false;
      if (cache.timestamp && Date.now() - cache.timestamp > MAX_AGE_MS) return false;

      var currentPage = getCurrentPage();
      var html = cache.pages
        .filter(function (entry) {
          var page = parsePage(entry.page);
          return page < currentPage && !hasLoadedPage(page) && entry.html;
        })
        .sort(function (a, b) { return parsePage(a.page) - parsePage(b.page); })
        .map(function (entry) { return entry.html; })
        .join('');

      if (!html) return false;

      grid.insertAdjacentHTML('afterbegin', html);
      document.documentElement.classList.add('orttu-restored-grid-cache');
      return true;
    } catch (e) {
      if (window.console && console.warn) console.warn('[ORTTU] cached collection restore failed', e);
      return false;
    }
  }

  function fetchPageHtml(page) {
    return fetch(pageUrl(page), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to fetch collection page ' + page);
        return response.text();
      })
      .then(function (html) {
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var remoteGrid = wrapper.querySelector('#product-grid');
        if (!remoteGrid) throw new Error('Missing #product-grid in page ' + page);
        var fragment = document.createElement('div');
        fragment.innerHTML = remoteGrid.innerHTML;
        markPageItems(fragment, page);
        return fragment.innerHTML;
      });
  }

  function updateUrlToSavedPage(data) {
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('page', data.productPage || data.loadedPage || getCurrentPage());
      window.history.replaceState(window.history.state || {}, '', url.href);
    } catch (e) {}
  }

  function scrollToTarget(data) {
    var target = getTarget(data.scrollToProduct);
    if (target) {
      var align = function () {
        target.scrollIntoView({ behavior: 'auto', block: 'center' });
      };

      // First align immediately so Back feels instant, then correct once after
      // images/scripts settle and card heights finish shifting.
      align();
      window.setTimeout(align, 220);
      window.setTimeout(function () {
        try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        restoring = false;
      }, 260);
      return true;
    }
    return false;
  }

  function fetchMissingPages(data) {
    var grid = getGrid();
    if (!grid) return Promise.resolve();

    var currentPage = getCurrentPage();
    var targetPage = data.productPage || data.loadedPage || currentPage;
    var maxLoadedPage = Math.max(data.loadedPage || targetPage, targetPage, currentPage);
    var pages = [];

    // New click tracking rewrites the Back URL to the clicked product's own
    // collection page, so the target should usually be server-rendered already.
    // For older saved states, fetch only the target page first; reconstructing a
    // whole page range before scrolling is what made Safari Back feel slow.
    if (data.productPage) {
      if (targetPage !== currentPage) pages.push(targetPage);
    } else {
      for (var r = 1; r <= maxLoadedPage; r += 1) {
        if (r !== currentPage) pages.push(r);
      }
    }

    if (!pages.length) return Promise.resolve();
    if (pages.length > MAX_RESTORE_PAGES) {
      // Avoid turning a back navigation into a huge network/DOM operation.
      pages = pages.slice(0, MAX_RESTORE_PAGES);
    }

    var before = pages.filter(function (page) { return page < currentPage; }).sort(function (a, b) { return a - b; });
    var after = pages.filter(function (page) { return page > currentPage; }).sort(function (a, b) { return a - b; });

    return Promise.all(before.map(fetchPageHtml)).then(function (beforeHtml) {
      if (beforeHtml.length) grid.insertAdjacentHTML('afterbegin', beforeHtml.join(''));
      return Promise.all(after.map(fetchPageHtml));
    }).then(function (afterHtml) {
      if (afterHtml.length) grid.insertAdjacentHTML('beforeend', afterHtml.join(''));
    });
  }

  function restore(sourceData) {
    if (restoring) return;
    var data = getStoredData(sourceData || window.history.state);
    if (!data) return;

    restoring = true;
    document.documentElement.classList.add('orttu-restoring-collection');

    restoreCachedPreviousPages(data);

    if (scrollToTarget(data, 0)) {
      document.documentElement.classList.remove('orttu-restoring-collection');
      return;
    }

    fetchMissingPages(data)
      .then(function () {
        if (!scrollToTarget(data, 1)) {
          if (typeof data.scrollY === 'number') {
            window.scrollTo({ top: data.scrollY, behavior: 'auto' });
          }
          restoring = false;
        }
        updateUrlToSavedPage(data);
      })
      .catch(function (error) {
        if (window.console && console.warn) console.warn('[ORTTU] collection restore failed', error);
        if (typeof data.scrollY === 'number') {
          window.scrollTo({ top: data.scrollY, behavior: 'auto' });
        }
        restoring = false;
      })
      .finally(function () {
        document.documentElement.classList.remove('orttu-restoring-collection');
      });
  }

  function scheduleRestore(sourceData, delay) {
    window.setTimeout(function () { restore(sourceData); }, typeof delay === 'number' ? delay : 80);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleRestore(window.history.state, 0); }, { once: true });
  } else {
    scheduleRestore(window.history.state, 0);
  }

  window.addEventListener('popstate', function (event) { scheduleRestore(event.state, 0); });
  window.addEventListener('pageshow', function (event) {
    scheduleRestore(window.history.state, event.persisted ? 0 : 0);
  });
})();
